import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { isBypassedEmail } from "@/hooks/useBypassAccess";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Sparkles, 
  Target, 
  TrendingUp, 
  Zap, 
  Users, 
  Award, 
  Dumbbell,
  Star,
  CheckCircle2,
  Quote,
  Camera,
  Trophy,
  Swords,
  BarChart3,
  Gift,
  MessageCircle,
  Smartphone,
  Monitor,
  ChevronDown,
  Shield,
  Clock,
  X,
  Flame,
  Crown,
  Plus,
  Minus
} from "lucide-react";

import dashboardPreview1 from "@/assets/dashboard-preview-1.png";
import dashboardPreview2 from "@/assets/dashboard-preview-2.png";
import dashboardPreview3 from "@/assets/dashboard-preview-3.png";

// ─── Data ────────────────────────────────────────────────────────────────────

const SCREENSHOTS = [dashboardPreview1, dashboardPreview2, dashboardPreview3];

const TIERS = [
  { name: "Sedentário Arrependido", emoji: "🛋️", points: 0 },
  { name: "Iniciante Motivado", emoji: "🌱", points: 100 },
  { name: "Guerreiro do Sofá", emoji: "⚔️", points: 300 },
  { name: "Atleta de Fim de Semana", emoji: "🏃", points: 600 },
  { name: "Rato de Academia", emoji: "🐀", points: 1000 },
  { name: "Máquina de Treino", emoji: "🤖", points: 1500 },
  { name: "Titã do Fitness", emoji: "💪", points: 2200 },
  { name: "Lenda Viva", emoji: "🌟", points: 3000 },
  { name: "Semideus", emoji: "⚡", points: 4000 },
  { name: "Imortal Fitness", emoji: "👑", points: 5500 },
];

const FEATURES_SHOWCASE = [
  {
    title: "IA que aprende com você",
    description: "Planos de treino e alimentação que evoluem junto com seu corpo e objetivos. A cada check-in, a IA refina as recomendações para acelerar seus resultados.",
    icon: Sparkles,
    accent: "from-emerald-400 to-cyan-400",
    color: "emerald",
    visual: "ai",
  },
  {
    title: "Foto → Calorias",
    description: "Tire uma foto da refeição e receba a análise nutricional instantaneamente. Proteínas, carboidratos, gorduras — tudo calculado em segundos.",
    icon: Camera,
    accent: "from-amber-400 to-orange-400",
    color: "amber",
    visual: "photo",
  },
  {
    title: "Gamificação & Rankings",
    description: "Suba do 'Sedentário Arrependido' ao 'Imortal Fitness' com pontos e ranks. Compete no ranking global e desbloqueie conquistas.",
    icon: Trophy,
    accent: "from-violet-400 to-purple-400",
    color: "violet",
    visual: "gamification",
  },
  {
    title: "Desafios 1v1",
    description: "Desafie amigos em batalhas fitness e prove quem é mais dedicado. Acompanhe o placar em tempo real.",
    icon: Swords,
    accent: "from-rose-400 to-pink-400",
    color: "rose",
    visual: "challenge",
  },
  {
    title: "Analytics completos",
    description: "Gráficos detalhados de peso, calorias, treinos e progresso ao longo do tempo. Visualize tendências e identifique o que funciona.",
    icon: BarChart3,
    accent: "from-blue-400 to-indigo-400",
    color: "blue",
    visual: "analytics",
  },
  {
    title: "FitPro Coach 24/7",
    description: "Converse com seu coach de IA a qualquer hora. Tire dúvidas, peça ajustes nos planos, receba motivação personalizada.",
    icon: MessageCircle,
    accent: "from-teal-400 to-emerald-400",
    color: "teal",
    visual: "coach",
  },
];

const STEPS = [
  { step: "01", title: "Faça o onboarding", description: "2 minutos para configurar seu perfil, objetivos e restrições.", icon: Target },
  { step: "02", title: "Receba seu plano", description: "A IA gera planos de treino e alimentação 100% personalizados.", icon: Sparkles },
  { step: "03", title: "Treine e suba no ranking", description: "Execute, registre, ganhe pontos e conquiste tiers.", icon: TrendingUp },
];

const TESTIMONIALS = [
  {
    name: "Carlos S.",
    result: "Perdeu 15kg em 4 meses",
    content: "O FitPro mudou minha vida! A IA realmente entende minhas necessidades e cria planos que funcionam de verdade.",
    rating: 5,
  },
  {
    name: "Ana Paula M.",
    result: "Ganhou 8kg de massa muscular",
    content: "Nunca imaginei que conseguiria resultados tão rápido. Os treinos são perfeitos pro meu nível e os planos alimentares são deliciosos.",
    rating: 5,
  },
  {
    name: "Roberto C.",
    result: "Mantém forma há 6 meses",
    content: "Mesmo com pouco tempo, consigo seguir a rotina. A gamificação é viciante — estou no tier 'Máquina de Treino' e não quero parar!",
    rating: 5,
  },
  {
    name: "Juliana F.",
    result: "Perdeu 10kg pós-gestação",
    content: "O chat com o coach me ajudou muito nos dias difíceis. É como ter um personal disponível 24h. Recomendo demais!",
    rating: 5,
  },
];

const FAQS = [
  {
    q: "O que é o FitPro IA?",
    a: "É um app de saúde e fitness que usa inteligência artificial para criar planos de treino e alimentação 100% personalizados, com gamificação, desafios sociais e um coach virtual disponível 24/7.",
  },
  {
    q: "Como a IA personaliza meus planos?",
    a: "No onboarding, você informa seus dados (peso, altura, objetivos, restrições). A IA usa essas informações para gerar planos únicos que se adaptam ao seu progresso.",
  },
  {
    q: "Posso cancelar a qualquer momento?",
    a: "Sim! Sem contratos ou fidelidade. Cancele quando quiser pelo portal de gerenciamento de assinatura.",
  },
  {
    q: "Funciona no iPhone e Android?",
    a: "Sim! O FitPro IA é um PWA (Progressive Web App) que funciona em qualquer dispositivo — iPhone, Android e Desktop. Instale direto do navegador, sem precisar de loja.",
  },
  {
    q: "Preciso de experiência em academia?",
    a: "Não! O app se adapta ao seu nível, desde iniciante até avançado. Os treinos são personalizados para seus equipamentos disponíveis e nível de experiência.",
  },
  {
    q: "Como funciona a gamificação?",
    a: "Você ganha pontos por cada ação (treinos, check-ins, metas). Conforme acumula pontos, sobe de tier — do 'Sedentário Arrependido' até o 'Imortal Fitness'. Compete no ranking global e desafie amigos!",
  },
];

const PAIN_POINTS = [
  { pain: "Dietas genéricas que não funcionam", solution: "Planos alimentares personalizados pela IA" },
  { pain: "Treinos sem direção nem objetivo", solution: "Workouts adaptados ao seu nível e meta" },
  { pain: "Falta de motivação e consistência", solution: "Gamificação, streaks e desafios sociais" },
  { pain: "Personal trainer caro e horário fixo", solution: "Coach de IA disponível 24/7 por R$ 29,90/mês" },
];

// ─── Animation variants ──────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemFade = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// ─── Component ───────────────────────────────────────────────────────────────

const Index = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFeature, setActiveFeature] = useState(0);
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    SCREENSHOTS.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // ─── Auth logic (preserved exactly) ──────────────────────────────────────
  useEffect(() => {
    let isMounted = true;

    const checkSubscription = async (session: any) => {
      try {
        if (isBypassedEmail(session.user?.email)) {
          if (!isMounted) return;
          setHasSubscription(true);
          navigate("/dashboard", { replace: true });
          return;
        }

        const { data } = await supabase.functions.invoke('check-subscription', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!isMounted) return;
        setHasSubscription(data?.subscribed || false);
        if (data?.subscribed) navigate("/dashboard", { replace: true });
      } catch (err) {
        console.error('Error checking subscription:', err);
        if (isMounted) setHasSubscription(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      setUser(session?.user ?? null);
      if (session) checkSubscription(session);
      else setHasSubscription(null);
    });

    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        setUser(session?.user ?? null);
        if (session) await checkSubscription(session);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    initializeAuth();
    return () => { isMounted = false; subscription.unsubscribe(); };
  }, [navigate]);

  const handleMainCTA = () => {
    if (user && hasSubscription) navigate("/dashboard");
    else if (user && !hasSubscription) navigate("/pricing");
    else navigate("/auth?tab=signup");
  };

  const scrollToFeatures = () => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <motion.div
          className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-400 flex items-center justify-center"
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        >
          <Dumbbell className="w-7 h-7 text-white" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden selection:bg-emerald-500/30">
      {/* ─── Navbar ────────────────────────────────────────────────────── */}
      <motion.nav
        className="fixed top-0 left-0 right-0 z-50 border-b border-white/5"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="backdrop-blur-xl bg-[#050505]/80">
          <div className="container mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-cyan-400 flex items-center justify-center">
                <Dumbbell className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg tracking-tight">FitPro</span>
            </div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                size="sm"
                className="bg-white text-black hover:bg-white/90 font-semibold rounded-full px-5"
                onClick={handleMainCTA}
              >
                {user && hasSubscription ? "Dashboard" : "Começar Agora"}
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.nav>

      <main>
        {/* ─── Hero ──────────────────────────────────────────────────── */}
        <section
          className="relative pt-32 pb-20 md:pt-44 md:pb-32 px-4"
        >
          {/* Gradient orbs */}
          <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-emerald-500/15 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute top-40 right-1/4 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none" />

          <div className="container mx-auto relative z-10 max-w-5xl">
            <div className="text-center space-y-8 animate-fade-in">
              {/* Badge */}
              <div className="flex justify-center">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/5 text-emerald-400 text-sm">
                  <Flame className="w-3.5 h-3.5" />
                  <span>+10.000 usuários transformando seus corpos</span>
                </div>
              </div>

              {/* Headline */}
              <h1
                className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tight leading-[0.95]"
              >
                Seu Personal Trainer
                <br />
                com IA.{" "}
                <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-x">
                  No seu bolso
                </span>
                <br />
                <span className="text-white/40">24/7</span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg md:text-xl text-white/50 max-w-2xl mx-auto leading-relaxed">
                Planos de treino e alimentação 100% personalizados, gamificação que vicia e um coach de IA sempre disponível.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    size="lg"
                    className="text-base md:text-lg px-8 py-6 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-full shadow-[0_0_30px_rgba(16,185,129,0.4)] hover:shadow-[0_0_50px_rgba(16,185,129,0.6)] transition-shadow border-0"
                    onClick={handleMainCTA}
                  >
                    {user && hasSubscription ? "Ir para Dashboard" : "Começar Agora"}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    size="lg"
                    variant="ghost"
                    className="text-base md:text-lg px-8 py-6 text-white/60 hover:text-white hover:bg-white/5 rounded-full border border-white/10"
                    onClick={scrollToFeatures}
                  >
                    Ver como funciona
                    <ChevronDown className="ml-2 w-4 h-4" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* App Screenshots Carousel */}
            <div
              className="mt-16 md:mt-24 relative max-w-4xl mx-auto animate-fade-in"
              style={{ animationDelay: '0.3s', animationFillMode: 'both' }}
            >
              <div className="relative rounded-2xl overflow-hidden">
                <div className="absolute inset-0 pointer-events-none" />
                {SCREENSHOTS.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    loading="eager"
                    decoding="async"
                    alt={`Preview do app ${i + 1}`}
                    className={`w-full rounded-2xl object-cover transition-opacity duration-300 ${i === activeScreenshot ? 'relative z-10 opacity-100' : 'absolute inset-0 z-0 opacity-0 pointer-events-none'}`}
                  />
                ))}
              </div>
              {/* Dots */}
              <div className="flex justify-center gap-2 mt-4">
                {SCREENSHOTS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveScreenshot(i)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${i === activeScreenshot ? 'bg-emerald-400 scale-125' : 'bg-white/20 hover:bg-white/40'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ─── Device compatibility bar ──────────────────────────────── */}
        <section className="py-10 px-4 border-y border-white/5">
          <div className="container mx-auto flex flex-wrap items-center justify-center gap-8 text-white/30 text-sm">
            <div className="flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              <span>iOS</span>
            </div>
            <div className="flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              <span>Android</span>
            </div>
            <div className="flex items-center gap-2">
              <Monitor className="w-4 h-4" />
              <span>Desktop</span>
            </div>
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              ))}
              <span className="ml-1">4.9/5 • 2.400+ avaliações</span>
            </div>
          </div>
        </section>

        {/* ─── Problem → Solution ────────────────────────────────────── */}
        <section className="py-24 px-4">
          <div className="container mx-auto max-w-5xl">
            <motion.div className="text-center mb-16" variants={fadeUp} viewport={{ once: true }} initial="hidden" whileInView="visible">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Chega de perder tempo com o que{" "}
                <span className="text-white/30">não funciona</span>
              </h2>
              <p className="text-white/40 text-lg">Veja como o FitPro resolve cada problema</p>
            </motion.div>

            <motion.div
              className="space-y-4"
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {PAIN_POINTS.map((item, i) => (
                <motion.div
                  key={i}
                  variants={itemFade}
                  className="grid md:grid-cols-2 gap-4 md:gap-0 rounded-2xl border border-white/5 overflow-hidden"
                >
                  <div className="p-6 flex items-center gap-4 bg-red-500/5">
                    <X className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <span className="text-white/60">{item.pain}</span>
                  </div>
                  <div className="p-6 flex items-center gap-4 bg-emerald-500/5">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-white/90 font-medium">{item.solution}</span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ─── Features Interactive Showcase ─────────────────────────── */}
        <section id="features" className="py-24 px-4 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/[0.03] via-transparent to-transparent pointer-events-none" />
          <div className="container mx-auto max-w-6xl relative">
            <motion.div className="text-center mb-16" variants={fadeUp} viewport={{ once: true }} initial="hidden" whileInView="visible">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Tudo que você precisa.{" "}
                <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                  Nada que não precisa.
                </span>
              </h2>
              <p className="text-white/40 text-lg">Clique para explorar cada funcionalidade</p>
            </motion.div>

            <motion.div
              className="grid md:grid-cols-[280px_1fr] lg:grid-cols-[320px_1fr] gap-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={stagger}
            >
              {/* Feature tabs (left) */}
              <motion.div className="flex flex-row md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0 no-scrollbar" variants={itemFade}>
                {FEATURES_SHOWCASE.map((f, i) => {
                  const Icon = f.icon;
                  const isActive = activeFeature === i;
                  return (
                    <button
                      key={i}
                      onClick={() => setActiveFeature(i)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-300 flex-shrink-0 md:flex-shrink ${
                        isActive
                          ? 'bg-white/[0.08] border border-white/10 shadow-[0_0_30px_rgba(16,185,129,0.1)]'
                          : 'bg-transparent border border-transparent hover:bg-white/[0.03]'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${f.accent} flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${isActive ? 'scale-110' : 'scale-100 opacity-60'}`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <span className={`text-sm font-medium transition-colors whitespace-nowrap md:whitespace-normal ${isActive ? 'text-white' : 'text-white/40'}`}>
                        {f.title}
                      </span>
                      {isActive && (
                        <motion.div
                          className="hidden md:block ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400"
                          layoutId="featureDot"
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </button>
                  );
                })}
              </motion.div>

              {/* Feature detail (right) */}
              <motion.div variants={itemFade} className="relative min-h-[320px] md:min-h-[380px]">
                {FEATURES_SHOWCASE.map((f, i) => {
                  const Icon = f.icon;
                  if (activeFeature !== i) return null;
                  return (
                    <motion.div
                      key={f.title}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.4 }}
                      className="rounded-2xl border border-white/5 bg-white/[0.02] p-8 md:p-10 h-full flex flex-col justify-between overflow-hidden relative"
                    >
                      {/* Background glow */}
                      <div className={`absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-br ${f.accent} opacity-[0.07] rounded-full blur-[80px] pointer-events-none`} />

                      <div className="relative z-10">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${f.accent} flex items-center justify-center mb-6 shadow-lg`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold mb-3 text-white">{f.title}</h3>
                        <p className="text-base text-white/50 leading-relaxed max-w-lg">{f.description}</p>
                      </div>

                      {/* Mini visual preview */}
                      <div className="relative z-10 mt-8">
                        {f.visual === "ai" && (
                          <div className="flex gap-3">
                            {["Plano personalizado", "Adapta ao progresso", "Aprende seus hábitos"].map((t, j) => (
                              <motion.div
                                key={j}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: j * 0.15 }}
                                className="px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400"
                              >
                                ✓ {t}
                              </motion.div>
                            ))}
                          </div>
                        )}
                        {f.visual === "photo" && (
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                              <Camera className="w-8 h-8 text-amber-400" />
                            </div>
                            <div className="flex-1">
                              <motion.div className="h-2 rounded-full bg-amber-500/20 mb-2" initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 0.8 }} />
                              <div className="flex gap-4 text-xs text-white/40">
                                <span>P: <span className="text-amber-400">32g</span></span>
                                <span>C: <span className="text-amber-400">45g</span></span>
                                <span>G: <span className="text-amber-400">12g</span></span>
                                <span className="text-amber-400 font-semibold">380 kcal</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {f.visual === "gamification" && (
                          <div className="flex items-center gap-2">
                            {["🛋️", "🌱", "⚔️", "🏃", "🐀", "🤖"].map((e, j) => (
                              <motion.div
                                key={j}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: j * 0.08, type: "spring" }}
                                className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm ${j <= 4 ? 'bg-violet-500/15 border border-violet-500/20' : 'bg-violet-500/30 border border-violet-400/40 ring-2 ring-violet-400/30'}`}
                              >
                                {e}
                              </motion.div>
                            ))}
                            <span className="text-xs text-violet-400 ml-1">+4</span>
                          </div>
                        )}
                        {f.visual === "challenge" && (
                          <div className="flex items-center gap-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/15">
                            <div className="text-center"><div className="text-lg font-bold text-rose-400">Você</div><div className="text-2xl font-black text-white">7</div></div>
                            <div className="flex-1 text-center text-xs text-white/30">
                              <Swords className="w-5 h-5 mx-auto mb-1 text-rose-400" />
                              Treinos na semana
                            </div>
                            <div className="text-center"><div className="text-lg font-bold text-rose-400/60">Ana</div><div className="text-2xl font-black text-white/50">5</div></div>
                          </div>
                        )}
                        {f.visual === "analytics" && (
                          <div className="flex gap-1 items-end h-16">
                            {[30, 45, 38, 52, 48, 65, 72, 60, 78, 85, 70, 90].map((h, j) => (
                              <motion.div
                                key={j}
                                className="flex-1 bg-gradient-to-t from-blue-500 to-indigo-400 rounded-sm"
                                initial={{ height: 0 }}
                                animate={{ height: `${h}%` }}
                                transition={{ duration: 0.5, delay: j * 0.05 }}
                              />
                            ))}
                          </div>
                        )}
                        {f.visual === "coach" && (
                          <div className="space-y-2">
                            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="bg-teal-500/10 border border-teal-500/15 rounded-xl rounded-bl-sm px-3 py-2 text-xs text-teal-400 max-w-[70%]">
                              Como ajustar meu treino para focar em hipertrofia?
                            </motion.div>
                            <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="bg-white/5 border border-white/10 rounded-xl rounded-br-sm px-3 py-2 text-xs text-white/60 max-w-[80%] ml-auto">
                              Baseado no seu perfil, recomendo 4 séries de 8-12 reps com carga progressiva... 💪
                            </motion.div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </motion.div>

            {/* Extra features pills */}
            <motion.div
              className="mt-8 flex flex-wrap justify-center gap-3"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {[
                { icon: "📊", label: "Wrapped mensal" },
                { icon: "📱", label: "Instale como app (PWA)" },
                { icon: "🎯", label: "Metas personalizadas" },
                { icon: "🥗", label: "Substituições inteligentes" },
              ].map((extra, i) => (
                <div key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/5 bg-white/[0.02] text-sm text-white/40">
                  <span>{extra.icon}</span>
                  <span>{extra.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ─── How It Works ──────────────────────────────────────────── */}
        <section className="py-24 px-4">
          <div className="container mx-auto max-w-4xl">
            <motion.div className="text-center mb-16" variants={fadeUp} viewport={{ once: true }} initial="hidden" whileInView="visible">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                3 passos.{" "}
                <span className="text-white/30">Zero complicação.</span>
              </h2>
            </motion.div>

            <motion.div className="grid md:grid-cols-3 gap-8" variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div key={i} variants={itemFade} className="text-center space-y-4">
                    <div className="text-5xl font-black text-white/20">{s.step}</div>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/10 flex items-center justify-center mx-auto">
                      <Icon className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-semibold">{s.title}</h3>
                    <p className="text-sm text-white/40 leading-relaxed">{s.description}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* ─── Testimonials ──────────────────────────────────────────── */}
        <section className="py-24 px-4 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-500/[0.02] to-transparent pointer-events-none" />
          <div className="container mx-auto max-w-6xl relative">
            <motion.div className="text-center mb-16" variants={fadeUp} viewport={{ once: true }} initial="hidden" whileInView="visible">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Histórias de{" "}
                <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                  transformação
                </span>
              </h2>
              <p className="text-white/40 text-lg">Resultados reais de pessoas reais</p>
            </motion.div>

            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-4"
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {TESTIMONIALS.map((t, i) => (
                <motion.div
                  key={i}
                  variants={itemFade}
                  whileHover={{ y: -4 }}
                  className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 space-y-4"
                >
                  <div className="flex gap-0.5">
                    {[...Array(t.rating)].map((_, j) => (
                      <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-white/60 leading-relaxed italic">"{t.content}"</p>
                  <div className="pt-2 border-t border-white/5">
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-emerald-400">{t.result}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ─── Gamification Tiers ─────────────────────────────────────── */}
        <section className="py-24 px-4">
          <div className="container mx-auto max-w-4xl">
            <motion.div className="text-center mb-16" variants={fadeUp} viewport={{ once: true }} initial="hidden" whileInView="visible">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Até onde você vai chegar?
              </h2>
              <p className="text-white/40 text-lg">10 tiers para conquistar. De sedentário a imortal.</p>
            </motion.div>

            <motion.div
              className="space-y-2"
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {TIERS.map((tier, i) => (
                <motion.div
                  key={i}
                  variants={itemFade}
                  className="flex items-center gap-4 rounded-xl border border-white/5 bg-white/[0.02] px-5 py-3 hover:bg-white/[0.04] transition-colors"
                >
                  <span className="text-2xl">{tier.emoji}</span>
                  <span className="flex-1 font-medium text-sm text-white/80">{tier.name}</span>
                  <span className="text-xs text-white/30">{tier.points}+ pts</span>
                  <div className="hidden sm:block w-32 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 rounded-full"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${Math.min(100, (i + 1) * 10)}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: i * 0.05 }}
                    />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ─── Pricing ───────────────────────────────────────────────── */}
        <section className="py-24 px-4 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-500/[0.03] to-transparent pointer-events-none" />
          <div className="container mx-auto max-w-4xl relative">
            <motion.div className="text-center mb-16" variants={fadeUp} viewport={{ once: true }} initial="hidden" whileInView="visible">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Simples e transparente
              </h2>
              <p className="text-white/40 text-lg">Um plano. Tudo incluso. Sem surpresas.</p>
            </motion.div>

            <motion.div
              className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto"
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {/* Monthly */}
              <motion.div variants={itemFade} className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 flex flex-col">
                <div>
                  <div className="text-sm text-white/40 mb-1">Mensal</div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold">R$ 29,90</span>
                    <span className="text-white/30 text-sm">/mês</span>
                  </div>
                </div>
                <div className="space-y-3 mt-6 flex-1">
                  {["Planos de treino IA", "Planos alimentares IA", "Coach IA 24/7", "Gamificação completa", "Desafios e rankings", "Análise de foto de refeição", "Wrapped mensal"].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-white/60">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
                <Button
                  className="w-full rounded-full bg-white/10 hover:bg-white/15 text-white border border-white/10 mt-6"
                  onClick={() => navigate("/auth?tab=signup")}
                >
                  Assinar Mensal
                </Button>
              </motion.div>

              {/* Annual */}
              <motion.div variants={itemFade} className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 flex flex-col relative overflow-hidden">
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-semibold">
                  Economize 17%
                </div>
                <div>
                  <div className="text-sm text-emerald-400/60 mb-1">Anual</div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold">R$ 24,99</span>
                    <span className="text-white/30 text-sm">/mês</span>
                  </div>
                  <div className="text-xs text-white/30 mt-1">R$ 299,90 cobrados anualmente</div>
                </div>
                <div className="space-y-3 mt-6 flex-1">
                  {["Tudo do plano mensal", "Economia de R$ 58,90/ano", "Prioridade no suporte", "Acesso antecipado a novidades", "Badges exclusivos", "Relatório anual premium", "Suporte prioritário"].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-white/70">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
                <Button
                  className="w-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_40px_rgba(16,185,129,0.5)] transition-shadow border-0 mt-6"
                  onClick={() => navigate("/auth?tab=signup")}
                >
                  Assinar Anual — Melhor valor
                </Button>
              </motion.div>
            </motion.div>

            <motion.div
              className="flex flex-wrap items-center justify-center gap-6 mt-8 text-xs text-white/30"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" />
                Pagamento seguro via Stripe
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                Cancele quando quiser
              </div>
              <div className="flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" />
                Acesso imediato
              </div>
            </motion.div>
          </div>
        </section>

        {/* ─── FAQ ────────────────────────────────────────────────────── */}
        <section className="py-24 px-4">
          <div className="container mx-auto max-w-3xl">
            <motion.div className="text-center mb-16" variants={fadeUp} viewport={{ once: true }} initial="hidden" whileInView="visible">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Perguntas frequentes
              </h2>
            </motion.div>

            <motion.div className="space-y-2" variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {FAQS.map((faq, i) => (
                <motion.div
                  key={i}
                  variants={itemFade}
                  className="rounded-xl border border-white/5 bg-white/[0.02] overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between p-5 text-left"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-medium text-sm text-white/80 pr-4">{faq.q}</span>
                    <motion.div
                      animate={{ rotate: openFaq === i ? 45 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Plus className="w-4 h-4 text-white/30 flex-shrink-0" />
                    </motion.div>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: openFaq === i ? "auto" : 0, opacity: openFaq === i ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 text-sm text-white/40 leading-relaxed">{faq.a}</div>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ─── Final CTA ─────────────────────────────────────────────── */}
        <section className="py-32 px-4 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/[0.05] to-transparent pointer-events-none" />
          <div className="container mx-auto max-w-3xl relative text-center">
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-6xl font-extrabold leading-tight">
                Pronto para transformar
                <br />
                <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                  seu corpo?
                </span>
              </h2>
              <p className="text-white/40 text-lg max-w-xl mx-auto">
                Junte-se a milhares de pessoas que já alcançaram seus objetivos com o FitPro.
              </p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  size="lg"
                  className="text-lg px-10 py-7 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-full shadow-[0_0_40px_rgba(16,185,129,0.4)] hover:shadow-[0_0_60px_rgba(16,185,129,0.6)] transition-shadow border-0"
                  onClick={() => navigate("/auth?tab=signup")}
                >
                  Começar Agora — É rápido
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
              <p className="text-xs text-white/20">Sem contratos. Cancele quando quiser.</p>
            </motion.div>
          </div>
        </section>

        {/* ─── Footer ────────────────────────────────────────────────── */}
        <footer className="py-12 px-4 border-t border-white/5">
          <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-emerald-400 to-cyan-400 flex items-center justify-center">
                <Dumbbell className="w-3 h-3 text-white" />
              </div>
              <span className="font-semibold text-sm text-white/60">FitPro</span>
            </div>
            <div className="flex items-center gap-6 text-xs text-white/30">
              <Link to="/terms" className="hover:text-white/60 transition-colors">Termos de uso</Link>
              <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacidade</Link>
              <Link to="/help" className="hover:text-white/60 transition-colors">Suporte</Link>
            </div>
            <p className="text-xs text-white/20">© 2026 FitPro. Todos os direitos reservados.</p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Index;
